// v3_fix_issues.md #3 - plain-language explanations for the status values shown around the app,
// surfaced as hover tooltips (see InfoTip). Keyed by the raw enum value.

export const DEPOSIT_STATUS_HELP = {
  PENDING: 'This booking includes a refundable security deposit, but the customer\'s payment has not been received yet — nothing is being held.',
  HELD: 'The refundable security deposit is being held for the duration of the rental. It is released once the item is returned and checked over.',
  REFUND_PENDING: 'The rental has ended (or the booking was cancelled) and the deposit refund has been approved — it just hasn\'t been sent back to the customer yet.',
  REFUNDED: 'The full security deposit has been returned to the customer.',
  PARTIALLY_DEDUCTED: 'Part of the deposit is being withheld to cover a reported damage or a late return; the remainder is still refundable.',
  NOT_APPLICABLE: 'This booking carries no security deposit — the owner did not require one on this listing.'
};

export const PAYOUT_STATUS_HELP = {
  NOT_DUE: 'The platform does not owe the rental owner a payout for this order yet — it becomes due after the rental completes, and only when the platform collected the payment.',
  PENDING: 'The rental is complete and the platform owes the rental owner their earnings — the transfer hasn\'t been sent yet.',
  PROCESSING: 'The payout to the rental owner has been initiated and is in transit.',
  PAID: 'The rental owner has been paid their earnings for this order.',
  FAILED: 'The payout transfer to the rental owner failed and needs to be retried.'
};

export const PAYMENT_STATE_HELP = {
  RECEIVABLE: 'No payment has been recorded for this order yet — it is still owed.',
  PENDING_CONFIRMATION: 'An amount was recorded but is not confirmed. A payment only counts as received once it has a method and a reference (transaction id / UTR / cash receipt no.).',
  PARTIALLY_RECEIVED: 'Only part of the order amount has been received and confirmed.',
  RECEIVED: 'The full order amount has been received and confirmed with a method and a reference.',
  FAILED: 'The last payment attempt failed — the amount is still owed.',
  REFUNDED: 'The payment for this order has been refunded to the customer.',
  PARTIALLY_REFUNDED: 'Part of the payment for this order has been refunded to the customer.'
};

export const RETURN_STATUS_HELP = {
  NOT_DUE: 'The rental is still running — the return window has not opened yet.',
  RETURN_SCHEDULED: 'The customer has arranged a date to send the item back.',
  PICKED_UP: 'The item has been collected from the customer and is on its way back.',
  RECEIVED: 'The item is back with the owner and is being checked over.',
  INSPECTION_PENDING: 'The item is back and awaiting a condition check before the booking is closed.',
  COMPLETED: 'The item has been returned and checked — this booking is finished.',
  CANCELLED: 'The booking was cancelled, so no return is due.'
};

export const DELIVERY_STATUS_HELP = {
  NOT_REQUIRED: 'This booking does not need a delivery.',
  PENDING: 'Delivery is being arranged — no one is out with the item yet.',
  PICKED_UP: 'The delivery person has collected the item from the owner.',
  IN_TRANSIT: 'The item is on its way to the customer.',
  DELIVERED: 'The item has been handed over to the customer.',
  FAILED: 'The delivery attempt failed and needs to be re-arranged.',
  CANCELLED: 'The booking was cancelled, so no delivery is happening.'
};

export function deliveryStatusReason(booking) {
  if (booking.deliveryStatus !== 'PENDING') return DELIVERY_STATUS_HELP[booking.deliveryStatus];
  const when = booking.deliveryAssignment?.scheduledAt || booking.startDate;
  const timing = when ? ` Planned for ${new Date(when).toLocaleString()}.` : ' A delivery time has not been scheduled yet.';
  if (booking.deliveryNeedsReassignment) return `The assigned delivery role is unavailable and a replacement is required.${timing}`;
  if (!booking.deliveryAssignment?.assignedTo) return `Awaiting assignment to the delivery role.${timing}`;
  return `Assigned to the delivery role, but pickup has not been marked started.${timing}`;
}

export const RECONCILIATION_HELP =
  'A rental owner recorded this payment themselves. Finance still needs to confirm it against the actual money movement.';
